import InfoLabel from '@/components/InfoLabel';
import IEventType from '@/types/IEventType';
import IResource from '@/types/IResource';
import {
	Avatar,
	Box,
	Button,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	Heading,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
} from '@chakra-ui/react';
import Select, { ControlProps, GroupBase, OptionProps } from 'react-select';
import Creatable from 'react-select/creatable';
import { useAddMemberModal } from './add-participant.controller';
import { NoAvailabilityBanner } from './components/no-availability.view';

interface IProps {
	eventType: IEventType;
	isOpen: boolean;
	close: () => void;
}

type IOption = IResource;

export default function AddParticipantModal(props: IProps) {
	const { availabilitiesQuery, form, handleSubmit, t } = useAddMemberModal(props);

	if (!availabilitiesQuery.data) {
		return null;
	}

	const email = form.watch('email')?.value ?? null;

	const options = availabilitiesQuery.data.resources;
	const emails = [...new Set(options?.map((r) => r.owner.email))].map((e) => ({ label: e, value: e })) ?? [];
	const filteredOptions = options.filter((o) => o.owner.email === email);

	return (
		<>
			<Modal isOpen={props.isOpen} onClose={props.close} size="2xl" isCentered>
				<ModalOverlay />
				<ModalContent minH="md">
					<ModalHeader>
						<Heading size="lg">{t('Add attendee')}</Heading>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text mb={4}>{t('The booking calendar will have free sots when this person is available.')}</Text>
						<Grid gap={4} as="form" id="form-add-participant" onSubmit={handleSubmit}>
							<FormControl>
								<InfoLabel text={t('Email')} help={t('')} />
								<Creatable
									formatCreateLabel={(input) => `${t('Invite {{email}}', { email: input })}`}
									isClearable
									isMulti={false}
									autoFocus
									required
									options={emails}
									value={form.watch('email')}
									onChange={(v) => {
										form.setValue('email', v);
										const firstResource = options.filter((o) => o.owner.email === v?.value).at(0);

										if (firstResource) {
											form.setValue('resource', firstResource);
										}
									}}
								/>
								<FormHelperText>
									{t('For privacy, only emails from available contacts in your teams are displayed.')}
								</FormHelperText>
							</FormControl>

							{filteredOptions.length > 0 ? (
								<FormControl>
									<FormLabel>{t('Availability')}</FormLabel>
									<Select
										{...form.register('resource', { required: true })}
										options={filteredOptions}
										isClearable
										getOptionLabel={(option) => option.name}
										filterOption={(candidate, input) => {
											return (
												candidate.data.name.toLowerCase().includes(input) ||
												candidate.data.owner.email.toLowerCase().includes(input)
											);
										}}
										isMulti={false}
										components={{ Option, Control }}
										value={form.watch('resource')}
										onChange={(e) => {
											form.setValue('resource', e, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
										}}
									/>
									<FormHelperText>
										{t('Select the availability that best fits the purpose of this reservation calendar.')}
									</FormHelperText>
								</FormControl>
							) : (
								<NoAvailabilityBanner email={email} />
							)}
						</Grid>
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={props.close}>
							{t('Close')}
						</Button>
						<Button
							form="form-add-participant"
							isDisabled={!form.formState.isValid}
							isLoading={form.formState.isSubmitting}
							colorScheme="primary"
							type="submit"
						>
							{t('Add attendee')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}

function Option(props: OptionProps<IOption, boolean>) {
	return (
		<Box
			display="flex"
			gap={4}
			_hover={{ bg: 'gray.50' }}
			bg={props.isFocused ? 'gray.50' : 'none'}
			cursor="pointer"
			onClick={() => props.selectOption(props.data)}
			py={2}
			px={4}
			ref={props.innerRef}
			{...props.innerProps}
		>
			<Avatar size="sm" name={props.data.name} />
			<Flex direction="column" fontSize="xs">
				<Text fontWeight="medium">
					{props.data.tags?.tt_default ? '⭐️' : ''} {props.data.name}
				</Text>
				<Text color="gray.500">{props.data.owner.email}</Text>
			</Flex>
		</Box>
	);
}

function Control(props: ControlProps<IResource, false, GroupBase<IResource>>) {
	return (
		<Box
			transition="all .2s"
			p={0.5}
			display="grid"
			gridTemplateColumns="1fr auto"
			borderRadius="md"
			borderColor={props.isFocused ? 'blue.500' : 'gray.300'}
			borderWidth={1}
			boxShadow={props.isFocused ? '0 0 0 1px var(--chakra-colors-blue-500)' : 'none'}
			{...props.innerProps}
			ref={props.innerRef}
		>
			{props.children}
		</Box>
	);
}
